import {CardEnum, PlayerColor, TradeEmbargoState, TradeOfferState} from '@colonist/game-states'
import {GameSpeedSettings, TradeEmbargoControllerReader} from '@colonist/game-base-types'
import {PlayerValidator} from '../PlayerValidator'
import {TradeActionStatus, TradeOfferOpponentDisplay, TradePlayerCreatingCounterOfferStatus, TradePlayerResponseStatus} from './TradeTypes'
import {TradeUtils} from './TradeUtils'
import {ArrayUtils, RandomUtils, TimeUtils, GameTimerUtils} from '@colonist/utils'

export class TradeValidator {
    readonly state: TradeOfferState
    private readonly currentTurnPlayerColors: PlayerColor[]
    private readonly playerStates: Map<PlayerColor, PlayerValidator>
    readonly embargoState: {[key in PlayerColor]?: TradeEmbargoState}
    private counterOffersEnabled: boolean

    constructor(state: TradeOfferState, currentTurnPlayerColors: PlayerColor[], playerStates: PlayerValidator[], embargoState: {[key in PlayerColor]?: TradeEmbargoState}) {
        this.state = state
        this.currentTurnPlayerColors = currentTurnPlayerColors

        this.playerStates = new Map()
        for(const playerState of playerStates) {
            this.playerStates.set(playerState.color, playerState)
        }

        this.embargoState = embargoState
        this.counterOffersEnabled = true
    }

    /**
     * About tradeExecutorColor:
    *  * Returning the first element of the current turn player colors array is a bad practice.
    *  * It is fine for now because counter offers are only enabled in games where there is only one current turn player.
    *  * This should be replaced by this.state.counterOfferInResponseToPlayerColor to enable counter offers in games with multiple current turn players.
    */
    get tradeExecutorColor(): PlayerColor {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        if(isCounterOffer) return this.currentTurnPlayerColors[0]
        return this.state.creator
    }

    getGivingResources(player: PlayerColor): CardEnum[] {
        const isCounterOfferByNonTradeExecutor = this.isCounterOfferByNonTradeExecutor()

        if(this.isTradeExecutor(player)) return isCounterOfferByNonTradeExecutor ? this.state.wantedResources : this.state.offeredResources
        return isCounterOfferByNonTradeExecutor ? this.state.offeredResources : this.state.wantedResources
    }

    getReceivingResources(player: PlayerColor): CardEnum[] {
        const isCounterOfferByNonTradeExecutor = this.isCounterOfferByNonTradeExecutor()

        if(this.isTradeExecutor(player)) return isCounterOfferByNonTradeExecutor ? this.state.offeredResources : this.state.wantedResources
        return isCounterOfferByNonTradeExecutor ? this.state.wantedResources : this.state.offeredResources
    }

    getRecipientColor(player: PlayerColor): PlayerColor {
        if(this.isTradeExecutor(player)) return PlayerColor.None
        return this.tradeExecutorColor
    }

    getPlayerResponseStates(): TradePlayerResponseStatus[] {
        const playerResponsesStates: TradePlayerResponseStatus[] = []
        for(const playerColor in this.state.playerResponses) {
            playerResponsesStates.push({player: Number(playerColor), responseStatus: this.state.playerResponses[playerColor]})
        }
        return playerResponsesStates
    }

    getPlayerResponseState(player: PlayerColor): TradePlayerResponseStatus | undefined {
        const playerResponse = this.state.playerResponses[player]
        if(playerResponse == undefined) return undefined
        return {player: Number(player), responseStatus: playerResponse}
    }

    getPlayersCreatingCounterOffer(): TradePlayerCreatingCounterOfferStatus[] {
        const oldPlayersCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus[] = []
        for(const playerColor in this.state.playersCreatingCounterOffer) {
            oldPlayersCreatingCounterOffer.push({player: Number(playerColor), isCreatingCounterOffer: this.state.playersCreatingCounterOffer[playerColor]})
        }
        return oldPlayersCreatingCounterOffer
    }

    getExecuteTradeActionStatus(player: PlayerColor): TradeActionStatus {
        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(!this.isTradeExecutor(player)) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.cannotExecuteOpenEndedTrade'},
            }
        }

        const requiredResources = this.getTradeExecutorOfferedResources()

        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources)
        if(!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.invalidResources'},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getAcceptTradeActionStatus(player: PlayerColor): TradeActionStatus {
        if(this.isTradeExecutor(player)) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.isOpenEndedTrade()) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player)
        if(doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getTradeExecutorPlayerUsername()}},
            }
        }

        const requiredResources = this.getTradeExecutorWantedResources()
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResources)
        if(!doesPlayerHaveRequiredResources) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.invalidResources'},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    isPlayerUnableToAcceptTrade(player: PlayerColor): boolean {
        if(player == PlayerColor.None) return false

        const isTradeExecutor = this.isTradeExecutor(player)
        return isTradeExecutor ? this.isTradeExecutorUnableToAcceptTrade(player) : this.isOtherPlayerUnableToAcceptTrade(player)
    }

    getTradeAutoRejectionTime(gameSpeed: GameSpeedSettings, playerColor: PlayerColor): number {
        const doIHaveNoCardsToTrade = this.doesPlayerHaveAnyCardToTrade(playerColor) == false
        if(doIHaveNoCardsToTrade) return 0

        const defaultTimeToAutoReject = GameTimerUtils.getDefaultTradeAutoRejectionTimeByGameSpeed(gameSpeed)

        const canPlayerAccept = this.isPlayerUnableToAcceptTrade(playerColor) == false
        if(canPlayerAccept) return defaultTimeToAutoReject

        const adjustmentFactor = 0.75
        const randomizationRange = TimeUtils.MILLISECONDS
        const timeToAutoRejectIfUnableToAccept = defaultTimeToAutoReject * adjustmentFactor
        const randomizedAutoRejectionTime = RandomUtils.randomNumberBetweenFloat(timeToAutoRejectIfUnableToAccept - randomizationRange, timeToAutoRejectIfUnableToAccept + randomizationRange)
        return randomizedAutoRejectionTime
    }

    private isTradeExecutor(player: PlayerColor): boolean {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined

        if(!isCounterOffer) {
            return this.state.creator == player
        }

        const isMyTurn = this.currentTurnPlayerColors.includes(player)
        return isMyTurn
    }

    private doesPlayerHaveAnyCardToTrade(player: PlayerColor): boolean {
        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) return true

        return playerStateController.resourceCards.getNumberOfCardsInInventory() > 0
    }

    private isTradeExecutorUnableToAcceptTrade(player: PlayerColor): boolean {
        if(this.isOpenEndedTrade()) return false
        if(this.getExecuteTradeActionStatus(player).canTakeAction) return false

        return true
    }

    private isOtherPlayerUnableToAcceptTrade(player: PlayerColor): boolean {
        if(this.isOpenEndedTrade()) return this.shouldOtherPlayerFastAutoRejectOpenEndedTrade(player)
        if(this.getAcceptTradeActionStatus(player).canTakeAction) return false

        return true
    }

    private shouldOtherPlayerFastAutoRejectOpenEndedTrade(player: PlayerColor): boolean {
        const playerStateController = this.playerStates.get(player)
        if(playerStateController == undefined) return false

        const requiredResources = this.getTradeExecutorWantedResources()

        if(requiredResources.length > playerStateController.resourceCards.getNumberOfCardsInInventory()) return true

        const numberOfRequiredAnyCards = ArrayUtils.countOccurenceOfValueInArray(requiredResources, CardEnum.AnyResource)
        const areAllWantedCardsWildCards = numberOfRequiredAnyCards == requiredResources.length
        if(areAllWantedCardsWildCards) return false

        const requiredResourcesWithoutAnyCard = ArrayUtils.removeAllOccurrenceFromArray(requiredResources, CardEnum.AnyResource)
        const doesPlayerHaveRequiredResources = playerStateController.hasResourceCardsInHand(requiredResourcesWithoutAnyCard)
        if(!doesPlayerHaveRequiredResources) return true

        return false
    }

    getProposeCounterOfferActionStatus(player: PlayerColor): TradeActionStatus {
        if(player === PlayerColor.None) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player)

        if(!this.counterOffersEnabled) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(this.state.creator == player) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        if(doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getTradeExecutorPlayerUsername()}},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getRejectTradeActionStatus(player: PlayerColor): TradeActionStatus {
        if(player === PlayerColor.None) {
            return {
                canTakeAction: false,
                displayActionButton: false,
            }
        }

        const doesTradeExecutorHaveActiveEmargoAgainstPlayer = TradeEmbargoControllerReader.doesPlayer1HaveAnEmbargoAgainstPlayer2(this.embargoState, this.tradeExecutorColor, player)

        if(doesTradeExecutorHaveActiveEmargoAgainstPlayer) {
            return {
                canTakeAction: false,
                displayActionButton: true,
                errorMessageIfCantTakeAction: {key: 'strings:game.errors.tradeErrors.youAreEmbargoed', options: {playerUsername: this.getTradeExecutorPlayerUsername()}},
            }
        }

        return {
            canTakeAction: true,
            displayActionButton: true,
        }
    }

    getOfferComponentsToDisplay(player: PlayerColor): TradeOfferOpponentDisplay {
        if(this.isOpenEndedTrade()) return TradeOfferOpponentDisplay.AvatarContainer

        const isSpectator = player == PlayerColor.None
        if(isSpectator) return TradeOfferOpponentDisplay.AvatarContainer

        if(this.isTradeExecutor(player)) return TradeOfferOpponentDisplay.ResponseButtons

        return TradeOfferOpponentDisplay.AvatarContainer
    }

    isOpenEndedTrade(): boolean {
        return TradeUtils.isOpenEndedTrade(this.state.offeredResources, this.state.wantedResources)
    }

    getTradeExecutorOfferedResources(): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        return isCounterOffer ? this.state.wantedResources : this.state.offeredResources
    }

    getTradeExecutorWantedResources(): CardEnum[] {
        const isCounterOffer = this.state.counterOfferInResponseToTradeId != undefined
        return isCounterOffer ? this.state.offeredResources : this.state.wantedResources
    }

    private getTradeExecutorPlayerUsername(): string {
        const currentTurnPlayerState = this.playerStates.get(this.tradeExecutorColor)
        return currentTurnPlayerState?.username ?? ''
    }

    private isCounterOfferByNonTradeExecutor(): boolean {
        if(this.state.counterOfferInResponseToTradeId == undefined) return false
        return !this.isTradeExecutor(this.state.creator)
    }

    disableCounterOffers() {
        this.counterOffersEnabled = false
    }
}
